import {Component, OnDestroy, OnInit} from "@angular/core";
import {AuthenticationManager} from "./authentication";
import {combineLatest, filter, map, Observable, startWith, Subject, Subscription, takeUntil} from "rxjs";
import {LicensesManagerService} from "./licenses/licenses-manager.service";
import {Store} from "@ngrx/store";
import {UserActions} from "./state/user/user.actions";
import {DeviceDetectionService} from "./shared/services/device-detection.service";
import {environment} from "../environments/environment";
import {NavigationService} from "./shared";
import {NotificationsManagerService} from "./notifications/services/notifications-manager.service";
import {CallSessionManager} from "./shared/services/call-session-manager.service";
import {NavigationEnd, Router} from "@angular/router";
import {selectCallsState} from "./state/call/current-call.selector";
import {CallEntry} from "./state/call/current-call-state";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {

    private _userLoggedInSubscription: Subscription | null = null;
    private destroy$ = new Subject<void>();

    userLoggedIn$: Observable<boolean> = this.auth.userLoggedInUpdates();

    activeIncomingCall: CallEntry | null = null;
    secondaryIncomingCalls: CallEntry[] = [];
    isProduction = environment.production;

    sidebarOpened = false;

    constructor(
        private auth: AuthenticationManager,
        private licenses: LicensesManagerService,
        private store: Store,
        private deviceDetection: DeviceDetectionService,
        private navigation: NavigationService,
        private callSessionManager: CallSessionManager,
        private router: Router,
        notificationsManager: NotificationsManagerService
    ) {
        notificationsManager.setupListener();
    }

    ngOnInit() {
        if (environment.production) {
            this.checkForMobileRedirect();
        }

        this._userLoggedInSubscription = this.userLoggedIn$.subscribe(
            (loggedIn) => {
                this.updateUIForUser(loggedIn);
                if (loggedIn) {
                    this.retrieveUserLicenses();
                }
            }
        );

        this.listenForIncomingCallState();
    }

    ngOnDestroy(): void {
        this._userLoggedInSubscription?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleSidebar() {
        this.sidebarOpened = !this.sidebarOpened;
    }

    hideIncomingCallCard(callId: string | null) {
        if (this.activeIncomingCall?.callId === callId) {
            this.activeIncomingCall = null;
        }
        this.secondaryIncomingCalls = this.secondaryIncomingCalls.filter(c => c.callId !== callId);
    }

    trackByCallId(_index: number, call: CallEntry): string | null {
        return call.callId;
    }

    private updateUIForUser(logged: boolean) {
        if (!logged) {
            this.sidebarOpened = false;
        }
    }

    private checkForMobileRedirect(): void {
        if (this.deviceDetection.shouldRedirectToAppNotInstalled()) {
            this.navigation.navigateToAppNotInstalled();
        }
    }

    private retrieveUserLicenses() {
        // We retrieve user licenses in background
        this.store.dispatch(UserActions.startRetrieving());
        this.licenses.getUserLicenses().subscribe({
            next: (licenses) =>
                this.store.dispatch(UserActions.saveLicenses({licenses: licenses})),
            error: (_) => this.store.dispatch(UserActions.errorRetrieving()),
        });
    }

    /**
     * Build the incoming-call-cards to show.
     *
     * - Active call (left): shown only if NOT on its destination dashboard
     * - Non-active calls (right, stacked): always shown
     *
     * Re-evaluates on both Redux state changes and route navigations.
     */
    private listenForIncomingCallState() {
        const navigation$ = this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            startWith(null)
        );

        combineLatest([
            this.store.select(selectCallsState),
            navigation$
        ]).pipe(
            takeUntil(this.destroy$)
        ).subscribe(([callsState]) => {
            const activeCalls = callsState.calls.filter(c => c.mainLeg != null || c.pipLeg != null);
            const activeCallId = callsState.activeCallId;

            const activeCall = activeCalls.find(c => c.callId === activeCallId) ?? null;

            this.activeIncomingCall = activeCall && !this.isOnDestinationDashboard(activeCall.destinationId)
                ? activeCall : null;
            this.secondaryIncomingCalls = activeCalls.filter(c => c.callId !== activeCallId);
        });
    }

    private isOnDestinationDashboard(destinationId: string | null): boolean {
        if (!destinationId) return false;
        const url = this.router.url;
        return url.includes(`/units/${destinationId}/dashboard`)
            || url.includes(`/pbxs/${destinationId}/dashboard`);
    }
}
